import { css, styled } from 'canoe';
import throttle from 'lodash/throttle';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { createCache, createMirror, rebuild } from 'rrweb-snapshot';

import { useIncrement } from 'commons/hooks';
import { BuiltInComponents } from 'craft/constants';
import {
  setSnapshotState,
  setToolboxTab,
  useCurrentSnapshot,
  usePlacementMode,
  useToolboxState,
  useViewportBreakpoint,
  useViewportDom,
} from 'craft/state';
import { ToolboxTabs } from 'craft/types';
import { getSelectorFromIframe } from 'craft/utils/snapshot-iframe';
import { useLoadedSnapshots } from 'dashboard/pages/Share/hooks/useLoadedSnapshots';

import { SNAPSHOT_ROOT, SNAPSHOT_ROOT_FRAME } from './constants';
import { fitIframe } from './utils/fitIframe';
import { useSyncInlineDocument } from './utils/useSyncInlineDocument';
import { useSyncPreviewHighlight } from './utils/useSyncPreviewHighlight';

const mirror = createMirror();
const cache = createCache();

const SnapshotContainer = styled.div<{ locked: boolean }>`
  ${({ theme, locked }) => css`
    width: 100%;
    height: 100%;
    min-height: 100%;
    position: relative;

    ${
      locked &&
      css`
        pointer-events: none;
      `
    }
    #${SNAPSHOT_ROOT_FRAME} {
      width: 100%;
      height: 100%;
      position: absolute;
      inset: 0;

      ${
        locked &&
        css`
          pointer-events: none;
          z-index: -1;
        `
      }
      background: ${theme.colors.White};
      clip-path: inset(0px);
    }
  `}
`;

const IFrameOverlay = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0;
  z-index: 0;
`;

interface ActiveSnapshotProps {
  rootNodeName: string;
  syncIterator?: number;
}

export const ActiveSnapshot = React.memo(({ rootNodeName, syncIterator }: ActiveSnapshotProps) => {
  const [iframeRef, setIframeRef] = useState<HTMLIFrameElement>();
  const viewportBreakpoint = useViewportBreakpoint();
  const { viewportWrapperEl, documentEl, viewportContainerEl } = useViewportDom();
  const { snapshot, placement, isSnapshotLoaded } = useCurrentSnapshot();
  const { placementMode } = usePlacementMode();
  const { forceOpenToolbox } = useToolboxState();
  const rootRef = useRef<HTMLDivElement>(null);
  const { value: heightSyncIterator, increment, reset } = useIncrement(1);

  const { isTargetVisible } = useSyncInlineDocument({
    iframeRef,
    syncIterator:
      rootNodeName === BuiltInComponents.Document
        ? (syncIterator || 1) + heightSyncIterator
        : undefined,
  });

  useSyncPreviewHighlight({
    iframeRef,
    syncIterator:
      rootNodeName === BuiltInComponents.Document && isTargetVisible
        ? (syncIterator || 1) + heightSyncIterator
        : undefined,
  });

  const dispatch = useDispatch();
  const setSnapshotLoaded = useCallback(
    (value: boolean) => {
      dispatch(setSnapshotState(value));
    },
    [snapshot],
  );

  const { loadedSnapshots, loading: loadingSnapshots } = useLoadedSnapshots(snapshot);

  /** Handles snapshot loading */
  useEffect(() => {
    if (loadingSnapshots) {
      return;
    }

    const currentSnapshot = loadedSnapshots[viewportBreakpoint];
    const doc = iframeRef?.contentDocument;

    if (!currentSnapshot || !doc) {
      return;
    }

    setSnapshotLoaded(false);

    viewportWrapperEl?.scrollTo(0, 0);
    doc.body?.remove();
    rebuild(currentSnapshot, { doc, mirror, cache });

    // every time a new snapshot loads, reset height sync iterator
    reset();

    setTimeout(() => {
      setSnapshotLoaded(true);
    }, 1000);
  }, [iframeRef, loadedSnapshots, loadingSnapshots, viewportBreakpoint, viewportWrapperEl]);

  useEffect(() => {
    // hide overflow when we're not in placement mode
    if (iframeRef?.contentWindow) {
      iframeRef.contentWindow.document.documentElement.style.overflow = placementMode
        ? ''
        : 'hidden';
    }
  }, [iframeRef?.contentWindow, placementMode]);

  useEffect(() => {
    // if we load a placement with a selector not visible on page for an inline snapshot
    // force to edit placement
    if (
      rootNodeName === BuiltInComponents.Document &&
      placement &&
      isSnapshotLoaded &&
      !getSelectorFromIframe(placement.selector)
    ) {
      dispatch(setToolboxTab(ToolboxTabs.Placements));
      // when automatically switching to placements tab, we want to show the toolbox
      forceOpenToolbox();
    }
  }, [placement, rootNodeName, isSnapshotLoaded]);

  const handleIframeRef = (ref: any) => {
    if (ref && !iframeRef) {
      setIframeRef(ref);
    }
  };

  // sync iframe height every second and on resize
  useEffect(() => {
    const snapshotRootEl = rootRef.current;

    if (
      !isSnapshotLoaded ||
      !iframeRef ||
      !viewportWrapperEl ||
      !viewportContainerEl ||
      !snapshotRootEl
    ) {
      /* eslint-disable no-param-reassign */
      if (snapshotRootEl) {
        snapshotRootEl.style.minHeight = '';
        snapshotRootEl.style.minWidth = '';
      }
      if (viewportContainerEl) {
        // to prevent flashing in case we do have to add overflow
        viewportContainerEl.style.overflowX = 'hidden';
      }
      /* eslint-enable no-param-reassign */

      return () => undefined;
    }

    const i = setInterval(() => {
      fitIframe(
        iframeRef,
        snapshotRootEl,
        viewportWrapperEl,
        viewportContainerEl,
        documentEl,
        heightSyncIterator === 1,
      );
      increment();
    }, 1000);

    const onResize = throttle(() => {
      fitIframe(
        iframeRef,
        snapshotRootEl,
        viewportWrapperEl,
        viewportContainerEl,
        documentEl,
        heightSyncIterator === 1,
      );
      increment();
    }, 50);
    window.addEventListener('resize', onResize);

    return () => {
      clearInterval(i);
      window.removeEventListener('resize', onResize);
    };
  }, [
    iframeRef,
    viewportWrapperEl,
    viewportContainerEl,
    documentEl,
    rootRef.current,
    isSnapshotLoaded,
    heightSyncIterator,
  ]);

  if (!snapshot) {
    return null;
  }

  return (
    <SnapshotContainer ref={rootRef} id={SNAPSHOT_ROOT} locked={!placementMode}>
      {!placementMode && <IFrameOverlay />}
      <iframe id={SNAPSHOT_ROOT_FRAME} ref={handleIframeRef} title="snapshot" />
    </SnapshotContainer>
  );
});
